/* eslint-disable react-hooks/exhaustive-deps */
import { PlusOutlined, SlackOutlined } from '@ant-design/icons';
import {
  Avatar,
  Button,
  Form,
  Input,
  message,
  Modal,
  Select,
  Spin,
  Upload,
} from 'antd';
import { useContext, useEffect, useState } from 'react';
import { listSlackUsers, listUsers, updateUser } from 'services/api/workspace';
import ProfileContext from 'services/profileContext';
import { CLOUDINARY } from 'utils/constants';
import { getBase64, isAdmin, normFile } from 'utils/utils';

const EditUserModal = ({ user, open, onCancel }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState({});
  const { profile } = useContext(ProfileContext);
  const [slackUsers, setSlackUsers] = useState(null);

  const onFinish = (values) => {
    if (!values.managerId) {
      delete values.managerId;
    }
    if (!values.slackUserId) {
      values = { ...values, slackUserId: null };
    }

    updateUser({
      ...values,
      userId: user._id,
      ...(image && image.picUrl ? { profilePictureUrl: image.picUrl } : {}),
    })
      .then(() => {
        message.success('User updated successfully');
        onCancel(true);
      })
      .catch((e) => {
        console.error('Error updating user', e);
        message.error('Error updating user, please try again');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    const calls = [listUsers()];
    if (profile?.workspace?.slack?.teamId) {
      calls.push(listSlackUsers());
    }
    Promise.all(calls)
      .then(([res, resSlack]) => {
        setUsers(res.data);
        if (resSlack && resSlack.data) {
          setSlackUsers(resSlack.data);
        }
      })
      .catch((e) => {
        console.error('Error fetching users', e);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file!');
    }
    const isLt1M = file.size / 1024 / 1024 < 1;
    if (!isLt1M) {
      message.error('Image must be smaller than 1MB');
    }
    return isJpgOrPng && isLt1M;
  };

  const onChangeUpload = (info) => {
    if (info.file.status === 'uploading') {
      // setIsUploading(true);
      return;
    }
    if (info.file.status === 'done') {
      getBase64(info.file.originFileObj, (imageUrl) => {
        // setIsUploading(false);
        setImage({ imageUrl, picUrl: info.file.response.secure_url });
      });
    }
    if (info.file.status === 'error') {
      message.error('Error while uploading, please try again.');
      // setIsUploading(false);
      setImage({ imageUrl: null });
    }
  };

  return (
    <div>
      <Modal
        open={open}
        onCancel={onCancel}
        title="Edit User"
        destroyOnClose={true}
        footer={null}
      >
        <div>
          <Spin spinning={loading}>
            <Form
              layout="vertical"
              initialValues={{
                ...user?.profile,
                email: user?.email,
                role: user?.role,
                slackUserId: user?.slackUserId,
              }}
              onFinish={onFinish}
            >
              {user?.profile?.profilePictureUrl && (
                <div>
                  Current Profile Picture{' '}
                  <Avatar size={60} src={user.profile.profilePictureUrl} />
                </div>
              )}
              <Form.Item
                label={
                  user?.profile?.profilePictureUrl
                    ? 'Update the picture?'
                    : 'Upload a picture of the employee:'
                }
                valuePropName="profilePictureUrl"
                getValueFromEvent={normFile}
              >
                <Upload
                  beforeUpload={beforeUpload}
                  onChange={onChangeUpload}
                  maxCount={1}
                  data={{
                    upload_preset: CLOUDINARY.UPLOAD_PRESETS,
                  }}
                  listType="picture-card"
                  action={`https://api.cloudinary.com/v1_1/${CLOUDINARY.CLOUD_NAME}/upload`}
                >
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                </Upload>
              </Form.Item>
              <Form.Item label="First Name" name="firstName">
                <Input />
              </Form.Item>
              <Form.Item label="Last Name" name="lastName">
                <Input />
              </Form.Item>
              <Form.Item label="Job Title" name="jobTitle">
                <Input />
              </Form.Item>
              <Form.Item label="Work Email" name="email">
                <Input />
              </Form.Item>
              <Form.Item label="Manager" name="managerId">
                <Select
                  placeholder="Select a manager"
                  options={users.map((u) => ({
                    label: `${u.profile.firstName} ${u.profile.lastName}`,
                    value: u._id,
                  }))}
                />
              </Form.Item>
              <Form.Item label="Role" name="role">
                <Select
                  disabled={!isAdmin(profile)}
                  placeholder="Choose a role"
                  options={[
                    { value: 'USER', label: 'User' },
                    { value: 'ADMIN', label: 'Admin' },
                  ]}
                />
              </Form.Item>
              {profile?.workspace?.slack?.teamId && (
                <Form.Item
                  label={
                    <span>
                      <SlackOutlined />
                      &nbsp;Slack Profile
                    </span>
                  }
                  name="slackUserId"
                >
                  <Select
                    disabled={!isAdmin(profile)}
                    placeholder="Choose a Slack user"
                    options={slackUsers?.map((e) => ({
                      label: (
                        <span>
                          <SlackOutlined />
                          &nbsp;{`${e.profile.firstName} ${e.profile.lastName} (${e.email})`}
                        </span>
                      ),
                      value: e.slackUserId,
                    }))}
                    allowClear
                    showSearch
                    filterOption={(val, opt) => {
                      const name = `${opt.label.props.children[2]}`;
                      return name.toLowerCase().includes(val.toLowerCase());
                    }}
                  />
                </Form.Item>
              )}
              <div
                style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}
              >
                <Button type="default" htmlType="button" onClick={onCancel}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Save
                </Button>
              </div>
            </Form>
          </Spin>
        </div>
      </Modal>
    </div>
  );
};

export default EditUserModal;
